import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import Crumbs from '../components/Crumbs';
import Drill from '../study/Drill';
import { validateFieldModes } from '../../../shared/api';
import type { CardResult, ChapterSummary } from '../../../shared/types';

/**
 * One chapter's sections, fetched when it is opened rather than up front.
 *
 * A thirty-chapter book is thirty requests if the builder loads every section
 * list eagerly, and most of them are never looked at — the chapter toggle is
 * what people reach for. `catalogApi` caches, so opening the same chapter twice
 * costs one request.
 */
function ChapterSections({
  chapterId,
  picked,
  onToggle,
}: {
  chapterId: string;
  picked: Set<string>;
  onToggle: (id: string) => void;
}) {
  const api = useApi();
  const chapter = useAsync(() => catalogApi.chapter(chapterId, api), [chapterId, api]);

  if (chapter.loading) return <Loading what="sections" />;
  if (chapter.error || !chapter.data) {
    return <LoadError message={chapter.error ?? 'Could not load sections'} onRetry={chapter.reload} />;
  }

  return (
    <ul className="builder-sections">
      {chapter.data.sections.map((section) => {
        const on = picked.has(section.id);
        return (
          <li key={section.id}>
            <label className="builder-section">
              <input type="checkbox" checked={on} onChange={() => onToggle(section.id)} />
              <span className="builder-section-name">
                {section.number} {section.title}
              </span>
              <span className="muted">{section.cardCount} cards</span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Pick chapters or sections, then drill the lot.
 *
 * The deck is assembled server-side from the chosen section ids rather than by
 * fetching each section here: one request instead of thirty, and no partial
 * deck when one of them fails.
 *
 * Chapters are the unit most people want — "everything in chapters 1 to 3" —
 * so a chapter toggles all its sections at once, and individual sections stay
 * reachable underneath for the times that is too blunt.
 */
export default function SessionBuilderPage() {
  const { languageCode = '' } = useParams();
  const api = useApi();
  const { settings } = useSettings();

  const language = useAsync(() => catalogApi.language(languageCode, api), [languageCode, api]);

  // One book for now; the picker reads whichever the language leads with.
  const bookId = language.data?.books[0]?.id;
  const book = useAsync(
    () => (bookId ? catalogApi.book(bookId, api) : Promise.resolve(null)),
    [bookId, api],
  );

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [started, setStarted] = useState(false);
  /** The one chapter whose sections are showing, if any. */
  const [expanded, setExpanded] = useState<string | null>(null);

  const chapters = book.data?.chapters ?? [];

  const toggleSection = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleChapter = (chapter: ChapterSummary) =>
    setPicked((prev) => {
      const next = new Set(prev);
      const all = chapter.sectionIds.every((id) => next.has(id));
      // All on means the tap is "clear this chapter"; anything else means
      // "select the rest of it", which is what a half-ticked box should do.
      for (const id of chapter.sectionIds) {
        if (all) next.delete(id);
        else next.add(id);
      }
      return next;
    });

  const pickedIds = useMemo(() => [...picked], [picked]);

  const deck = useAsync(
    () => (started && pickedIds.length > 0 ? catalogApi.deck(pickedIds, api) : Promise.resolve(null)),
    [started, pickedIds.join(','), api],
  );

  const record = useCallback(
    (results: { cardId: string; result: CardResult }[]) =>
      progressApi.recordReviewSession(results, api),
    [api],
  );

  if (language.loading || book.loading) {
    return (
      <main className="page">
        <Loading what="the book" />
      </main>
    );
  }

  if (language.error || !language.data) {
    return (
      <main className="page">
        <LoadError message={language.error ?? 'Language not found'} onRetry={language.reload} />
      </main>
    );
  }

  const lang = language.data;

  // Running: the deck has been assembled, so hand off to the drill.
  if (started) {
    if (deck.loading) {
      return (
        <main className="page">
          <Loading what="your session" />
        </main>
      );
    }
    if (deck.error || !deck.data) {
      return (
        <main className="page">
          <LoadError
            message={deck.error ?? 'Could not build that session'}
            onRetry={() => setStarted(false)}
          />
        </main>
      );
    }

    const problem = validateFieldModes(settings.flashcards.fields, lang);
    if (problem) {
      return (
        <main className="page">
          <p className="error">{problem}</p>
          <Link to="/profile" className="btn">
            Back to the card setup
          </Link>
        </main>
      );
    }

    return (
      <Drill
        cards={deck.data.cards}
        language={lang}
        settings={settings}
        label={`${deck.data.cards.length} cards from ${deck.data.sectionIds.length} sections`}
        exitTo={`/build/${lang.code}`}
        exitLabel="Pick different sections"
        deckNoun="session"
        onRecord={record}
      />
    );
  }

  return (
    <main className="page">
      <Crumbs
        items={[
          { label: 'Home', to: '/' },
          { label: lang.name, to: `/languages/${lang.code}` },
          { label: 'Build a session' },
        ]}
      />

      <header className="page-header">
        <h1>Build a session</h1>
        <p className="page-description">
          Pick whatever you want to drill — a chapter, a handful of sections, or the lot. Tapping a
          chapter takes all of it.
        </p>
      </header>

      {book.error && <LoadError message={book.error} onRetry={book.reload} />}

      <div className="builder">
        {chapters.map((chapter) => {
          const chosen = chapter.sectionIds.filter((id) => picked.has(id)).length;
          const all = chosen === chapter.sectionIds.length && chosen > 0;
          const open = expanded === chapter.id;
          return (
            <section key={chapter.id} className="builder-chapter">
              <div className="builder-chapter-row">
                <button
                  type="button"
                  className={all ? 'btn btn--primary btn--small' : 'btn btn--small'}
                  onClick={() => toggleChapter(chapter)}
                >
                  {all ? '✓ ' : ''}Chapter {chapter.number}
                </button>
                <span className="builder-chapter-title">{chapter.title}</span>
                <span className="muted builder-chapter-count">
                  {chosen > 0
                    ? `${chosen} of ${chapter.sectionIds.length} picked`
                    : `${chapter.sectionCount} sections · ${chapter.cardCount} cards`}
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  aria-expanded={open}
                  onClick={() => setExpanded(open ? null : chapter.id)}
                >
                  {open ? 'Hide sections' : 'Sections'}
                </button>
              </div>

              {open && (
                <ChapterSections
                  chapterId={chapter.id}
                  picked={picked}
                  onToggle={toggleSection}
                />
              )}
            </section>
          );
        })}
      </div>

      {/* A sticky footer would be better on a phone, but the count has to be
          visible wherever you are in a thirty-chapter list. */}
      <div className="builder-bar">
        <span className="muted">
          {pickedIds.length === 0
            ? 'Nothing picked yet.'
            : `${pickedIds.length} ${pickedIds.length === 1 ? 'section' : 'sections'} picked`}
        </span>
        <span className="builder-bar-actions">
          {pickedIds.length > 0 && (
            <button type="button" className="btn" onClick={() => setPicked(new Set())}>
              Clear
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={pickedIds.length === 0}
            onClick={() => setStarted(true)}
          >
            Start the session
          </button>
        </span>
      </div>
    </main>
  );
}
